import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Typography } from '@mui/material';
import { PowerBIEmbed } from 'powerbi-client-react';
import { Embed, models } from 'powerbi-client';
import { acquirePowerBiToken } from './powerBiToken';

import styles from './powerbi.module.css';

interface PowerBIReportProps {
  /** Report configuration; the access token is acquired and refreshed here, so any `accessToken` supplied is ignored. */
  embedConfig: models.IReportEmbedConfiguration;
}

/** Renew this long before expiry. Must exceed MSAL's own renewal window (5 minutes) or the "renewal" would return the cached token. */
const REFRESH_LEAD_MS = 4 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

export default function PowerBIReport({ embedConfig: baseConfig }: PowerBIReportProps) {
  const [initialToken, setInitialToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const embedRef = useRef<Embed | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const run = async (isInitial: boolean) => {
      try {
        const result = await acquirePowerBiToken();
        if (cancelled) return;

        if (isInitial) {
          setInitialToken(result.accessToken);
        } else {
          // Pushed into the live report; changing embedConfig instead would tear down and re-embed it
          await embedRef.current?.setAccessToken(result.accessToken);
        }

        const delay = Math.max((result.expiresOn?.getTime() ?? 0) - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
        timer = window.setTimeout(() => void run(false), delay);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    };

    void run(true);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  // Only the first token goes in the config so later renewals do not change it
  const embedConfig = useMemo<models.IReportEmbedConfiguration | null>(
    () => (initialToken ? { ...baseConfig, accessToken: initialToken, tokenType: models.TokenType.Aad } : null),
    [baseConfig, initialToken]
  );

  if (error) return <Alert severity='error'>{error}</Alert>;
  if (!embedConfig) return <Typography>Authenticating with Power BI...</Typography>;

  return (
    <div className={styles.fill}>
      <PowerBIEmbed
        embedConfig={embedConfig}
        cssClassName={styles['report-container']}
        getEmbeddedComponent={(embed) => {
          embedRef.current = embed;
        }}
        eventHandlers={
          new Map([
            [
              'error',
              (event) => {
                console.error(event?.detail);
              }
            ]
          ])
        }
      />
    </div>
  );
}
